import React, { useMemo, useState } from "react";
import type { ManifestAsset } from "../types.js";
import { buildFolderTree, type TreeNode } from "../folderTree.js";

interface Props {
  assets: ManifestAsset[];
  selectedPath: string;
  onSelect: (path: string) => void;
  /** When set, display the tree rooted at this folder (locked-folder mode). */
  rootPath?: string;
  /** Signed-in curators only: right-click a folder → "Create magic link". */
  onMagicLink?: (path: string) => void;
}

function findNode(root: TreeNode, path: string): TreeNode | null {
  if (root.path === path) return root;
  for (const c of root.children) {
    const f = findNode(c, path);
    if (f) return f;
  }
  return null;
}

interface ContextMenuState {
  x: number;
  y: number;
  path: string;
  name: string;
}

export function FolderTree({ assets, selectedPath, onSelect, rootPath = "", onMagicLink }: Props) {
  // Protect rootPath from passthrough-collapse so the re-root below can find it.
  const fullTree = useMemo(() => buildFolderTree(assets, rootPath), [assets, rootPath]);
  const tree = useMemo(() => (rootPath ? findNode(fullTree, rootPath) ?? fullTree : fullTree), [fullTree, rootPath]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([tree.path]));
  const [menu, setMenu] = useState<ContextMenuState | null>(null);

  // When selectedPath changes, make sure its ancestors are expanded so the
  // selected node is visible. Never auto-collapse.
  React.useEffect(() => {
    if (!selectedPath) return;
    setExpanded((prev) => {
      const n = new Set(prev);
      const parts = selectedPath.split("/");
      for (let i = 0; i < parts.length; i++) n.add(parts.slice(0, i + 1).join("/"));
      return n;
    });
  }, [selectedPath]);

  // Any click or Escape anywhere dismisses the context menu.
  React.useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const toggle = (path: string) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(path)) n.delete(path);
      else n.add(path);
      return n;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    const walk = (n: TreeNode) => {
      all.add(n.path);
      n.children.forEach(walk);
    };
    walk(tree);
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set([tree.path]));

  // Right-click on a folder row: open the curator context menu (if enabled).
  const handleContextMenu = onMagicLink
    ? (e: React.MouseEvent, path: string, name: string) => {
        // Only real folders — the "All assets" home row of an unlocked tree
        // has path "" which is not a shareable scope; leave the browser's
        // native menu alone there.
        if (!path) return;
        e.preventDefault();
        e.stopPropagation();
        // Clamp so the menu never renders past the viewport edge (the tree
        // rail reaches the bottom of the screen; position:fixed can't scroll).
        const x = Math.min(e.clientX, window.innerWidth - 220);
        const y = Math.min(e.clientY, window.innerHeight - 110);
        setMenu({ x, y, path, name });
      }
    : undefined;

  // In locked mode the "home" row represents the locked folder itself.
  const homeLabel = rootPath ? tree.name || rootPath : "All assets";

  return (
    <aside className="folder-tree">
      <div className="tree-header">
        <h3>Folders</h3>
        <div className="tree-actions">
          <button className="tree-action-btn" onClick={expandAll}>Expand all</button>
          <button className="tree-action-btn" onClick={collapseAll}>Collapse all</button>
        </div>
      </div>
      <ul className="tree-root">
        <li>
          <div
            className={`tree-row all ${selectedPath === tree.path ? "selected" : ""}`}
            onClick={() => onSelect(tree.path)}
            onContextMenu={handleContextMenu && ((e) => handleContextMenu(e, tree.path, homeLabel))}
            role="treeitem"
            tabIndex={0}
          >
            <span className="tree-toggle spacer" />
            <span className="tree-icon">🏠</span>
            <span className="tree-label">{homeLabel}</span>
            <span className="tree-count">{tree.totalAssets}</span>
          </div>
          <ul>
            {tree.children.map((n) => (
              <TreeNodeRow
                key={n.path}
                node={n}
                depth={1}
                selectedPath={selectedPath}
                expanded={expanded}
                onToggle={toggle}
                onSelect={onSelect}
                onContextMenu={handleContextMenu}
              />
            ))}
          </ul>
        </li>
      </ul>
      {menu && (
        <div
          className="tree-context-menu"
          style={{ left: menu.x, top: menu.y }}
          role="menu"
        >
          <div className="tree-context-title" title={menu.path}>{menu.name}</div>
          <button
            role="menuitem"
            onClick={() => {
              setMenu(null);
              onMagicLink?.(menu.path);
            }}
          >
            Create magic link…
          </button>
        </div>
      )}
    </aside>
  );
}

interface RowProps {
  node: TreeNode;
  depth: number;
  selectedPath: string;
  expanded: Set<string>;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
  onContextMenu?: (e: React.MouseEvent, path: string, name: string) => void;
}

function TreeNodeRow({ node, depth, selectedPath, expanded, onToggle, onSelect, onContextMenu }: RowProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.path);
  const isSelected = selectedPath === node.path;

  // Clicking the whole row: select it AND toggle expansion (if it has children).
  // Matches Canto / most file-tree UX.
  const handleRowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onSelect(node.path);
    if (hasChildren) onToggle(node.path);
  };
  // Click just on the chevron: toggle only, no selection change.
  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(node.path);
  };

  return (
    <li>
      <div
        className={`tree-row ${isSelected ? "selected" : ""} ${hasChildren ? "has-children" : ""}`}
        style={{ paddingLeft: `${depth * 14}px` }}
        onClick={handleRowClick}
        onContextMenu={onContextMenu && ((e) => onContextMenu(e, node.path, node.name))}
        role="treeitem"
        aria-expanded={hasChildren ? isExpanded : undefined}
        tabIndex={0}
      >
        {hasChildren ? (
          <span
            className="tree-toggle"
            role="button"
            aria-label={isExpanded ? "Collapse" : "Expand"}
            onClick={handleToggleClick}
          >
            {isExpanded ? "▼" : "▶"}
          </span>
        ) : (
          <span className="tree-toggle spacer" />
        )}
        <span className="tree-icon">{hasChildren && isExpanded ? "📂" : "📁"}</span>
        <span className="tree-label">{node.name}</span>
        <span className="tree-count">{node.totalAssets}</span>
      </div>
      {hasChildren && isExpanded && (
        <ul>
          {node.children.map((c) => (
            <TreeNodeRow
              key={c.path}
              node={c}
              depth={depth + 1}
              selectedPath={selectedPath}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              onContextMenu={onContextMenu}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
